import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text, Icon, IconButton, Tooltip } from "@chakra-ui/react";
import {
  HiOutlineMagnifyingGlassPlus,
  HiOutlineMagnifyingGlassMinus,
  HiOutlineArrowsPointingOut,
} from "react-icons/hi2";
import CanvasBackground from "./CanvasBackground";
import { c, glassSurface } from "../../classroomColors";

/** What a press can land on that is not the canvas itself. */
const OWN_PRESSES =
  "[data-board-item], [data-resize-handle], [data-content-frame], button, a, input, textarea, select, [contenteditable='true'], [data-no-pan]";

const isTyping = (el) =>
  el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/** Dots / Plain. */
const PatternToggle = ({ value, onChange }) => (
  <Flex display="inline-flex" gap="2px" p="3px" borderRadius="200px" {...glassSurface}>
    {["dots", "plain"].map((key) => (
      <Box
        key={key}
        as="button"
        type="button"
        onClick={() => onChange(key)}
        px="14px"
        py="5px"
        borderRadius="200px"
        fontSize="12px"
        fontWeight={value === key ? "500" : "400"}
        bg={value === key ? c.surface : "transparent"}
        color={value === key ? c.text : c.textSub}
        boxShadow={value === key ? "0 1px 2px rgba(34,34,34,0.08)" : "none"}
        textTransform="capitalize"
        transition="background 0.15s, color 0.15s"
      >
        {key}
      </Box>
    ))}
  </Flex>
);

const ControlButton = ({ label, icon, onClick, shortcut }) => (
  <Tooltip
    label={shortcut ? `${label} · ${shortcut}` : label}
    placement="top"
    hasArrow
    fontSize="11px"
  >
    <IconButton
      aria-label={label}
      icon={<Icon as={icon} boxSize="15px" />}
      size="xs"
      minW="30px"
      h="30px"
      variant="ghost"
      borderRadius="full"
      color={c.textSub}
      _hover={{ bg: c.surface, color: c.text }}
      onClick={onClick}
    />
  </Tooltip>
);

const MOD = typeof navigator !== "undefined" && /Mac/.test(navigator.platform) ? "⌘" : "Ctrl";

/**
 * The endless canvas: the board, and everything opened onto it, under one
 * pan and zoom.
 *
 * Moving around works the way a whiteboard does (Miro, FigJam):
 *
 * - drag empty canvas with the cursor tool to move it; hold Space, or use
 *   the middle button, to move it with any tool armed;
 * - scroll (two fingers on a trackpad) to move; pinch, or hold ⌘/Ctrl and
 *   scroll, to zoom at the pointer;
 * - the zoom buttons and ⌘/Ctrl + / − zoom around the middle of the screen,
 *   so what you are looking at stays put; Fit frames everything on the
 *   board, and ⇧0 returns to 100%.
 *
 * Everything in board coordinates is in one transformed layer, so moving is
 * one CSS transform rather than arithmetic in every child. The chrome — the
 * canvas controls, and whatever the page lays over the top — stays put.
 *
 * It is the room itself: it fills whatever holds it, edge to edge.
 */
const InClassCanvas = ({
  viewport,
  onZoomAt,
  onZoomBy,
  onPanBy,
  onFit,
  onResetView,
  onStartPan,
  /** Whether a press on empty canvas moves it: true under the cursor tool
   *  and for anyone who cannot draw, false while a drawing tool is armed. */
  panOnEmpty = true,
  pattern,
  onPatternChange,
  isDark,
  /** A press that landed on the canvas rather than on anything that keeps
   *  its own presses — what lets go of a selected page. */
  onPressEmpty,
  children,
  overlay,
}) => {
  const hostRef = useRef(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [panning, setPanning] = useState(false);

  const centre = useCallback(() => {
    const rect = hostRef.current?.getBoundingClientRect();
    return rect ? { x: rect.width / 2, y: rect.height / 2 } : undefined;
  }, []);

  const zoomIn = useCallback(() => onZoomBy(1.25, centre()), [onZoomBy, centre]);
  const zoomOut = useCallback(() => onZoomBy(1 / 1.25, centre()), [onZoomBy, centre]);
  const fit = useCallback(() => {
    const rect = hostRef.current?.getBoundingClientRect();
    if (rect) onFit?.({ width: rect.width, height: rect.height });
  }, [onFit]);

  const beginPan = useCallback(
    (e) => {
      e.preventDefault();
      setPanning(true);
      onStartPan(e);
      const done = () => {
        setPanning(false);
        window.removeEventListener("pointerup", done);
      };
      window.addEventListener("pointerup", done);
    },
    [onStartPan]
  );

  // Wheel is bound by hand with { passive: false }: React's onWheel cannot
  // stop the page itself scrolling or zooming.
  const handleWheel = useCallback(
    (e) => {
      const rect = hostRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Something inside that scrolls on its own keeps its wheel.
      if (e.target.closest?.("[data-own-scroll]")) return;
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        // A pinch arrives as a ctrl-wheel of small steps; a mouse wheel with
        // ⌘/Ctrl as large ones. Scaled and capped so both feel the same.
        const factor = Math.min(1.25, Math.max(0.8, Math.exp(-e.deltaY * 0.01)));
        onZoomAt(factor, e.clientX - rect.left, e.clientY - rect.top);
      } else {
        onPanBy(-e.deltaX, -e.deltaY);
      }
    },
    [onZoomAt, onPanBy]
  );

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Space to move, and the zoom keys. Never while typing.
  useEffect(() => {
    const down = (e) => {
      if (isTyping(e.target)) return;
      // Space is the hand everywhere but a text field, as on any
      // whiteboard: a tool just clicked keeps focus, and must not swallow
      // it. Enter still presses a focused button.
      if (e.code === "Space") {
        e.preventDefault();
        if (!e.repeat) setSpaceHeld(true);
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "=" || e.key === "+")) {
        e.preventDefault();
        zoomIn();
      } else if (mod && e.key === "-") {
        e.preventDefault();
        zoomOut();
      } else if (e.shiftKey && !mod && (e.code === "Digit1" || e.key === "!")) {
        e.preventDefault();
        fit();
      } else if (e.shiftKey && !mod && (e.code === "Digit0" || e.key === ")")) {
        e.preventDefault();
        onResetView();
      }
    };
    const up = (e) => {
      if (e.code === "Space") setSpaceHeld(false);
    };
    const blur = () => setSpaceHeld(false);
    // Capture phase: a focused control (a tooltip's trigger, say) can stop
    // a key on its way up, and the hand should not depend on what has focus.
    window.addEventListener("keydown", down, true);
    window.addEventListener("keyup", up, true);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down, true);
      window.removeEventListener("keyup", up, true);
      window.removeEventListener("blur", blur);
    };
  }, [zoomIn, zoomOut, fit, onResetView]);

  // The middle button moves the canvas whatever is under it or armed.
  const handlePointerDownCapture = (e) => {
    if (e.button === 1) {
      e.stopPropagation();
      beginPan(e);
    }
  };

  // Reached only by presses nothing else kept: the board lets empty presses
  // through under the cursor tool, and items stop their own.
  const handlePointerDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest?.(OWN_PRESSES)) return;
    onPressEmpty?.();
    if (panOnEmpty) beginPan(e);
  };

  return (
    <Box
      ref={hostRef}
      position="absolute"
      inset="0"
      overflow="hidden"
      // White ground with the dots on it, as the solve paper has.
      bg={c.surface}
      onPointerDownCapture={handlePointerDownCapture}
      onPointerDown={handlePointerDown}
      sx={{ touchAction: "none" }}
    >
      <CanvasBackground pattern={pattern} viewport={viewport} isDark={isDark} />

      {/* everything in board coordinates */}
      <Box
        position="absolute"
        inset="0"
        transform={`translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`}
        transformOrigin="0 0"
        willChange="transform"
      >
        {children}
      </Box>

      {/* While Space is held, or a move is under way, one sheet over the board
          takes the pointer: the hand shows everywhere, and nothing under it
          is drawn on or picked up by mistake. Under the chrome. */}
      {(spaceHeld || panning) && (
        <Box
          position="absolute"
          inset="0"
          zIndex={11}
          cursor={panning ? "grabbing" : "grab"}
          onPointerDown={(e) => {
            if (e.button === 0) beginPan(e);
          }}
        />
      )}

      {/* The canvas's own controls, low on the right. */}
      <Flex position="absolute" bottom="20px" right="20px" zIndex={12} align="center" gap="10px">
        <PatternToggle value={pattern} onChange={onPatternChange} />
        <Flex align="center" gap="2px" p="3px" borderRadius="200px" {...glassSurface}>
          <ControlButton label="Zoom out" icon={HiOutlineMagnifyingGlassMinus} onClick={zoomOut} shortcut={`${MOD} −`} />
          <Tooltip
            label={`Back to 100% · ⇧0. Scroll to move, ${MOD} + scroll or pinch to zoom, Space + drag to pan.`}
            placement="top"
            hasArrow
            fontSize="11px"
            maxW="260px"
          >
            <Text
              as="button"
              onClick={onResetView}
              fontSize="11.5px"
              color={c.textSub}
              minW="44px"
              textAlign="center"
              sx={{ fontVariantNumeric: "tabular-nums" }}
              _hover={{ color: c.text }}
            >
              {Math.round(viewport.zoom * 100)}%
            </Text>
          </Tooltip>
          <ControlButton label="Zoom in" icon={HiOutlineMagnifyingGlassPlus} onClick={zoomIn} shortcut={`${MOD} +`} />
          <Box w="1px" h="16px" bg={c.borderSoft} mx="2px" />
          <ControlButton label="Fit everything" icon={HiOutlineArrowsPointingOut} onClick={fit} shortcut="⇧1" />
        </Flex>
      </Flex>

      {overlay}
    </Box>
  );
};

export default InClassCanvas;
